import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { SignIn } from './components/SignIn';
import { AuthenticatedRoute } from './components/AuthenticatedRoute';
import { Home } from './routes/Home';
import { GeoOverridesPage } from './routes/staff/GeoOverridesPage';
import { DomainDetailPage } from './routes/admin/DomainDetailPage';
import { PartnersLandingPage } from './routes/admin/PartnersLandingPage';
import { BriefDetailPage } from './routes/admin/BriefDetailPage';
import { UserManagementPage } from './routes/admin/UserManagementPage';
import { BetaInterestPage } from './routes/admin/BetaInterestPage';
import { AssignCustomersPage } from './routes/admin/AssignCustomersPage';
// ADR-113 D25 — the person-centric access screen. Asserted structurally by
// test/person-access-mounted.test.ts: it shipped with no route at all, mounted
// only in the render harness, and so was unreachable at the apex.
import { PersonAccessRedirect } from './routes/admin/PersonAccessRedirect';
import { CustomersPage } from './routes/admin/CustomersPage';
import { AdminCustomerDetailPage } from './routes/admin/AdminCustomerDetailPage';
import { NetworksPage } from './routes/admin/NetworksPage';
// ADR-075 — staff Cedar AAA: new business-role + agent COG surfaces (placeholder shells).
import { GrowthPage } from './routes/admin/GrowthPage';
import { GradingPage } from './routes/admin/GradingPage';
import { FinancePage } from './routes/admin/FinancePage';
import { AgentsPage } from './routes/admin/AgentsPage';
import { NexaConfigPage } from './routes/admin/NexaConfigPage';
import { MonitoringPage } from './routes/admin/MonitoringPage';
import { FlowValidatorPage } from './routes/admin/FlowValidatorPage';
import { AlertsPage } from './routes/admin/AlertsPage';
import { SnippetTelemetryPage } from './routes/admin/SnippetTelemetryPage';
import { LaunchpadFollowUpPage } from './routes/admin/LaunchpadFollowUpPage';
// The Test Bench (s3-grading-engine-node24) — an upgraded copy of a function tested against the live one.
import { TestBenchPage } from './routes/admin/TestBenchPage';
import { StaffProfilePage } from './routes/admin/StaffProfilePage';
import { ZoneContextProvider } from './components/ZoneContext';
import { AlertsBoardProvider } from './lib/alerts-board';
import { useTzPreference } from './lib/time-format';

/**
 * App shell.
 *
 * Routes:
 *   /signin                                                  — public sign-in
 *   /auth/callback                                           — OAuth landing
 *   /                                                        — authenticated landing
 *   /staff/geo-overrides                                     — 3b geo override admin
 *   /admin/customers                                         — the single customer console (ADR-090)
 *   /admin/customers/:accountId                              — customer detail
 *   /admin/customers/:accountId/domains/:domainToken         — domain detail (relocated, ADR-090)
 *   /staff/customers*                                        — retired → redirect to /admin/customers* (ADR-090)
 *   /admin/partners                                          — 5d-1 Partners landing
 *   /admin/users/:sub/access                                 — ADR-113 D25 → REDIRECTS into the
 *                                                                User Management drilldown (Am.3)
 *
 * Phase 4 [Retake] — /staff/applications routes removed alongside the
 * Phase 4 POW removal. Phase 5d-1 introduces /admin/partners as the new
 * canonical admin surface for the Channel Partner Reference §6 spec; the
 * admin-api endpoints (`GET/POST /admin/partner-applications`) stay wired
 * and now read from Nexa-PartnerBriefs (5c).
 */
export function App() {
  const { status } = useAuth();
  // Every date in the ops console is in the staff member's zone (lib/time-format.tsx). Subscribing here
  // re-renders the tree when the profile's Timezone preference arrives from /admin/me or is saved, so a
  // helper that reads the zone outside a hook never shows the device zone after the preference is known.
  useTzPreference();

  if (status === 'loading') {
    return <div className="portal-loading">Loading…</div>;
  }

  // ADR-123 Decision 1 — every page reports its zone context here and the navigation bar's
  // indicator reads it; one provider above every route so the two always meet.
  //
  // Quality register Q1 (30 October 2026) — the ONE alerts board the navbar indicator, the Home
  // widget and the Alerts page all read, above every route so it survives navigation and the
  // three can never be on different clocks.
  return (
    <ZoneContextProvider>
    <AlertsBoardProvider>
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/"
        element={
          <AuthenticatedRoute>
            <Home />
          </AuthenticatedRoute>
        }
      />
      {/* BOW-finesse B6 — the legacy Phase-3b /onboard/* full-page wizard
          (WizardLayout + Customer/Domain/Validate/Done steps) was removed. It
          was superseded by the in-place SOW/POW/BOW modal launchers on the
          dashboard (ADR-076; Home.tsx provisions via /portal/sow/provision).
          No live caller routed to /onboard/* — the only link was the deleted
          AdminTabStrip's Onboard tab. */}
      <Route
        path="/staff/geo-overrides"
        element={
          <AuthenticatedRoute>
            <GeoOverridesPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-090 — /staff/customers* is retired; the single console lives at
          /admin/customers*. Redirect any bookmarked staff-customer link (list,
          detail, domain drill-in) to the equivalent /admin path — no hard 404. */}
      <Route path="/staff/customers" element={<Navigate to="/admin/customers" replace />} />
      <Route path="/staff/customers/:accountId" element={<StaffCustomerRedirect />} />
      <Route path="/staff/customers/:accountId/domains/:domainToken" element={<StaffCustomerRedirect />} />
      <Route
        path="/admin/customers/:accountId/domains/:domainToken"
        element={
          <AuthenticatedRoute>
            <DomainDetailPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <AuthenticatedRoute>
            <PartnersLandingPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/partners/:applicationId"
        element={
          <AuthenticatedRoute>
            <BriefDetailPage />
          </AuthenticatedRoute>
        }
      />
      {/* 31 May 2026 — placeholder Admin COG items. Real implementations
          land in Phase 5d-3 (Users + Customers) and Phase 6+ (Networks +
          Nexa Config). Each route renders a "Coming soon" page inside
          the standard AppHeader-wrapped layout. */}
      <Route
        path="/admin/users"
        element={
          <AuthenticatedRoute>
            <UserManagementPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-098 — the Beta Interest work queue. */}
      <Route
        path="/admin/beta-interest"
        element={
          <AuthenticatedRoute>
            <BetaInterestPage />
          </AuthenticatedRoute>
        }
      />
      {/* F6 (Reference §4.3) — assign customers to a consultant, reached from
          Dashboard Access → the consultant's Manage. */}
      <Route
        path="/admin/users/portal-access/:sub/assign"
        element={
          <AuthenticatedRoute>
            <AssignCustomersPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-113 D25 (Amendment 3) — REDIRECT, not a screen. The access sections
          now live in the User Management drilldown, because D25 asks for ONE
          person-centric screen and one already existed; the standalone page was
          the second. The route is kept so a bookmarked or shared deep link still
          lands on that person rather than 404ing.
          Ranked below the more specific /admin/users/portal-access/:sub/assign
          by react-router's static-over-dynamic segment ranking; the two also
          differ in depth, so neither shadows the other. */}
      <Route
        path="/admin/users/:sub/access"
        element={
          <AuthenticatedRoute>
            <PersonAccessRedirect />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <AuthenticatedRoute>
            <CustomersPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/customers/:accountId"
        element={
          <AuthenticatedRoute>
            <AdminCustomerDetailPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/networks"
        element={
          <AuthenticatedRoute>
            <NetworksPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/monitoring"
        element={
          <AuthenticatedRoute>
            <MonitoringPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-075 — staff Cedar AAA: Growth / Finance business-role surfaces +
          Agents (ruleset management). Placeholder shells today. */}
      <Route
        path="/admin/growth"
        element={
          <AuthenticatedRoute>
            <GrowthPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-133 Amendment 2 §1 — COG → COMMUNITY → Grading (S9 · Community). */}
      <Route
        path="/admin/grading"
        element={
          <AuthenticatedRoute>
            <GradingPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/finance"
        element={
          <AuthenticatedRoute>
            <FinancePage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/agents"
        element={
          <AuthenticatedRoute>
            <AgentsPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/config"
        element={
          <AuthenticatedRoute>
            <NexaConfigPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/diags"
        element={
          <AuthenticatedRoute>
            <FlowValidatorPage />
          </AuthenticatedRoute>
        }
      />
      {/* The Test Bench (s3-grading-engine-node24) — super_admin only (test_bench:run); the page says so to others. */}
      <Route
        path="/admin/test-bench"
        element={
          <AuthenticatedRoute>
            <TestBenchPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-071 — general admin alerting surface (A3 detail view + snippet
          telemetry surface). */}
      <Route
        path="/admin/alerts"
        element={
          <AuthenticatedRoute>
            <AlertsPage />
          </AuthenticatedRoute>
        }
      />
      {/* telemetry-uat2 — My Profile (name, timezone, password) for staff. */}
      <Route
        path="/admin/profile"
        element={
          <AuthenticatedRoute>
            <StaffProfilePage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/admin/snippet-telemetry"
        element={
          <AuthenticatedRoute>
            <SnippetTelemetryPage />
          </AuthenticatedRoute>
        }
      />
      {/* ADR-127 D17 — paying customers whose snippet is still not live ~7 days after sign-up. */}
      <Route
        path="/admin/launchpad-follow-up"
        element={
          <AuthenticatedRoute>
            <LaunchpadFollowUpPage />
          </AuthenticatedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AlertsBoardProvider>
    </ZoneContextProvider>
  );
}

/**
 * /auth/callback — Amplify handles OAuth code exchange on app load via
 * Amplify Hub events. This component just waits and redirects to home.
 */
function AuthCallback() {
  return <Navigate to="/" replace />;
}

/**
 * ADR-090 — param-preserving redirect for the retired /staff/customers detail +
 * domain-drill-in paths. Maps to the equivalent /admin/customers* path so
 * bookmarked links land on the consolidated console rather than a 404.
 */
function StaffCustomerRedirect() {
  const { accountId, domainToken } = useParams<{ accountId: string; domainToken?: string }>();
  const base = `/admin/customers/${encodeURIComponent(accountId ?? '')}`;
  const to = domainToken ? `${base}/domains/${encodeURIComponent(domainToken)}` : base;
  return <Navigate to={to} replace />;
}
